import { observer } from 'mobx-react'
import React from 'react'

import { WorkOrderReadinessStatus } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { ChecklistStatus, pluralize, Tooltip } from '@avantstay/backoffice-core'

import { ExtendedColumnCell } from '@new/domains/fastBookingsTapeChart/__commonStyles'
import { CaseTabs } from '@new/domains/fastBookingsTapeChart/components/ExtendedColumns/components/ArrivalsChecklistSidePanel/ArrivalsChecklist.types'
import { arrivalsChecklistStore } from '@new/domains/fastBookingsTapeChart/components/ExtendedColumns/components/ArrivalsChecklistSidePanel/ArrivalsChecklistStore'
import { fastBookingsTapeChartFiltersStore } from '@new/domains/fastBookingsTapeChart/internal'
import { HomeExtendedInfo } from '@new/domains/fastBookingsTapeChart/stores/FastBookingsTapeChartStore'

import { SidePanelStore, SidePanelType } from '@stores/SidePanelStore'

import * as S from './CheckListStatus.styles'
import { getDueDatePeriod } from './CheckListStatus.utils'

interface CheckListStatusProps {
  caseTab?: CaseTabs
  checklistStatus?: WorkOrderReadinessStatus
  columnWidth: number
  caseWithHighOrUrgentPriorityCount?: number
  hasCases?: boolean
  home?: HomeExtendedInfo | undefined
  openCaseCount?: number
}

const _CheckListStatus = ({
  caseTab,
  checklistStatus,
  columnWidth,
  hasCases,
  caseWithHighOrUrgentPriorityCount,
  home,
  openCaseCount,
}: CheckListStatusProps) => {
  const {
    setArrivalChecklistWorkOrderIds,
    setCasesDueDatePeriod,
    setCleansAndInspectionsDueDatePeriod,
    setDefaultCaseTab,
    setSelectedArrivalChecklistHomeId,
  } = arrivalsChecklistStore
  const { endDate, startDate } = fastBookingsTapeChartFiltersStore

  const handleOnClick = () => {
    if (home) {
      const casesReferencePeriod =
        home.operationReadinessStatus?.othersCasesDetails?.referencePeriod
      const cleansAndInspectionsReferencePeriod =
        home.operationReadinessStatus?.cleanDetails?.referencePeriod

      const casesDueDatePeriod = getDueDatePeriod({
        dueDates: casesReferencePeriod,
        fallbackEndDate: endDate,
        fallbackStartDate: startDate,
      })
      const cleansAndInspectionsDueDatePeriod = getDueDatePeriod({
        dueDates: cleansAndInspectionsReferencePeriod,
        fallbackEndDate: endDate,
        fallbackStartDate: startDate,
      })

      const combinedCases = [
        ...(home?.operationReadinessStatus?.cleanDetails?.cases ?? []),
        ...(home?.operationReadinessStatus?.inspectedDetails?.cases ?? []),
        ...(home?.operationReadinessStatus?.othersCasesDetails?.cases ?? []),
      ]
      const workedOrderIds = combinedCases
        ? combinedCases.map(({ workOrderId }) => workOrderId)
        : undefined

      setSelectedArrivalChecklistHomeId(home.id)
      setArrivalChecklistWorkOrderIds(workedOrderIds)
      setDefaultCaseTab(caseTab)
      setCasesDueDatePeriod(casesDueDatePeriod)
      setCleansAndInspectionsDueDatePeriod(cleansAndInspectionsDueDatePeriod)
      SidePanelStore.setCurrentSidePanel(SidePanelType.arrivalsChecklist, {
        screenHasSecondaryMenu: true,
      })
    }
  }

  const renderChecklistStatus = () => {
    if (hasCases) {
      if (openCaseCount) {
        return (
          <Tooltip
            content={
              <>
                {openCaseCount} {pluralize(openCaseCount, 'Cases', 'Case')} Due Before Check-Out
                {caseWithHighOrUrgentPriorityCount ? (
                  <>
                    <br />
                    {caseWithHighOrUrgentPriorityCount} High/Urgent{' '}
                    {pluralize(caseWithHighOrUrgentPriorityCount, 'Cases', 'Case')}
                  </>
                ) : null}
              </>
            }
            placement="top"
          >
            <S.CaseCountContainer hasWarningCircle={!!caseWithHighOrUrgentPriorityCount}>
              {openCaseCount}
            </S.CaseCountContainer>
          </Tooltip>
        )
      }

      if (checklistStatus) {
        return <ChecklistStatus checklistStatus={checklistStatus} />
      }
    }

    return '-'
  }

  return (
    <ExtendedColumnCell width={columnWidth} color="unset" isClickable onClick={handleOnClick}>
      {renderChecklistStatus()}
    </ExtendedColumnCell>
  )
}

export const CheckListStatus = observer(_CheckListStatus)
